import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, Heart, Ruler, Minus, Plus, ChevronRight, Tag, AlertTriangle } from 'lucide-react';
import { getProduct, toggleWishlist } from '../lib/api';
import { optimize } from '../lib/imageOptimizer';
import ProductImg from '../components/ProductImg';
import { useCart } from '../lib/cart';
import { useAuth } from '../lib/auth';
import SizeGuideModal from '../components/SizeGuideModal';
import { toast } from 'sonner';

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customNumber, setCustomNumber] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getProduct(slug);
        setProduct(res.data.product);
        setRelated(res.data.related || []);
        setSelectedSize('');
        setSelectedImage(0);
        setQuantity(1);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [slug]);

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error('Veuillez sélectionner une taille');
      return;
    }
    const customization = customName || customNumber ? `${customName} ${customNumber}`.trim() : '';
    const customSurcharge = (customName ? 2.99 : 0) + (customNumber ? 2.99 : 0);
    const finalPrice = product.price + customSurcharge;
    addItem({ ...product, price: finalPrice }, selectedSize, quantity, customization);
    toast.success('Ajouté au panier');
  };

  const handleWishlist = async () => {
    if (!user) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }
    try {
      const res = await toggleWishlist(product.product_id);
      toast.success(res.data.action === 'added' ? 'Ajouté aux favoris' : 'Retiré des favoris');
    } catch { toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-zinc-100 animate-pulse" />
          <div className="space-y-4"><div className="h-8 bg-zinc-100 animate-pulse w-3/4" /><div className="h-6 bg-zinc-100 animate-pulse w-1/4" /></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 text-center">
        <p className="text-zinc-500 font-body text-lg">Produit non trouvé</p>
        <Link to="/catalogue" className="btn-primary inline-block mt-4 bg-zinc-900 text-white font-bold text-sm px-6 py-3 uppercase tracking-wider">
          Retour au catalogue
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="product-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-zinc-500 font-body mb-6" data-testid="breadcrumb">
        <Link to="/" className="hover:text-zinc-900 transition-colors">Accueil</Link>
        <ChevronRight size={12} />
        <Link to="/catalogue" className="hover:text-zinc-900 transition-colors">Catalogue</Link>
        <ChevronRight size={12} />
        <span className="text-zinc-900">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <div className="space-y-3">
          <div className="aspect-square bg-zinc-50 overflow-hidden border border-zinc-200" data-testid="product-main-image">
            <ProductImg src={product.images[selectedImage]} alt={product.name} width={700} eager />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`w-16 h-16 border overflow-hidden ${i === selectedImage ? 'border-zinc-900' : 'border-zinc-200'}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <ProductImg src={img} alt="" width={80} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="font-body">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{product.team} &mdash; {product.season}</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-4" data-testid="product-title">{product.name}</h1>

          {/* Price */}
          <div className="flex items-center gap-3 mb-3" data-testid="product-price">
            <span className="text-2xl font-bold">{product.price.toFixed(2)} €</span>
          </div>

          {/* Promo banner */}
          <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 px-3 py-2 mb-6 flex items-center gap-2" data-testid="product-promo">
            <Tag size={14} className="text-[#FF3B30] shrink-0" />
            <p className="text-xs font-body text-[#FF3B30] font-medium">Offre spéciale : 2 maillots achetés, le 3ème offert</p>
          </div>

          {/* Stock */}
          {product.stock_count <= 10 && (
            <p className="text-sm text-[#FF3B30] mb-4 animate-pulse-stock" data-testid="stock-indicator">
              Plus que {product.stock_count} en stock !
            </p>
          )}

          {/* Sizes */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold uppercase tracking-wider">Taille</span>
              <button onClick={() => setShowSizeGuide(true)} className="text-xs text-zinc-400 hover:text-zinc-900 transition-colors flex items-center gap-1" data-testid="guide-tailles-toggle">
                <Ruler size={12} /> Guide des tailles
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="tailles-list">
              {product.sizes.map(s => (
                <button
                  key={s}
                  onClick={() => setSelectedSize(s)}
                  className={`min-w-[44px] h-10 px-3 text-sm font-bold border transition-colors ${s === selectedSize ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-400 hover:text-zinc-900 hover:border-zinc-400'}`}
                  data-testid={`size-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Size advisory — informative, never blocks purchase */}
            <div className="mt-3 bg-amber-50 border-l-2 border-amber-500 px-3 py-2.5 flex gap-2 text-[11px] sm:text-xs leading-relaxed" data-testid="size-advisory">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-amber-900 font-body w-full">
                <p>
                  <strong className="font-bold">Conseil taille :</strong> nos articles taillent généralement petit. Nous recommandons de prendre <strong>une taille au-dessus</strong>, voire deux selon votre morphologie.
                </p>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="size-mapping">
                  <div className="bg-amber-100/60 border border-amber-300/60 px-2 py-1 flex items-center justify-center gap-1.5 text-[11px]">
                    <span className="text-amber-700 line-through decoration-amber-500/60">S</span>
                    <span className="text-amber-900">→</span>
                    <strong className="text-amber-900">M</strong>
                  </div>
                  <div className="bg-amber-100/60 border border-amber-300/60 px-2 py-1 flex items-center justify-center gap-1.5 text-[11px]">
                    <span className="text-amber-700 line-through decoration-amber-500/60">M</span>
                    <span className="text-amber-900">→</span>
                    <strong className="text-amber-900">L</strong>
                  </div>
                  <div className="bg-amber-100/60 border border-amber-300/60 px-2 py-1 flex items-center justify-center gap-1.5 text-[11px]">
                    <span className="text-amber-700 line-through decoration-amber-500/60">L</span>
                    <span className="text-amber-900">→</span>
                    <strong className="text-amber-900">XL</strong>
                  </div>
                  <div className="bg-amber-100/60 border border-amber-300/60 px-2 py-1 flex items-center justify-center gap-1.5 text-[11px]">
                    <span className="text-amber-700 line-through decoration-amber-500/60">XL</span>
                    <span className="text-amber-900">→</span>
                    <strong className="text-amber-900">2XL</strong>
                  </div>
                </div>
                <p className="mt-2">
                  En cas de doute, consultez le guide des tailles en{' '}
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(true)}
                    className="underline font-bold text-amber-900 hover:text-amber-700"
                    data-testid="size-advisory-link"
                  >
                    cliquant ici
                  </button>.
                </p>
              </div>
            </div>
          </div>

          {/* Size guide modal */}
          <SizeGuideModal open={showSizeGuide} onClose={() => setShowSizeGuide(false)} />

          {/* Customization */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold uppercase tracking-wider">Personnalisation</span>
              <span className="text-xs text-zinc-400 font-body">+2,99 € par champ</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Nom (ex: MBAPPÉ)"
                value={customName}
                onChange={(e) => setCustomName(e.target.value.toUpperCase())}
                className="bg-transparent border border-zinc-200 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none"
                maxLength={15}
                data-testid="custom-name"
              />
              <input
                type="text"
                placeholder="Numéro (ex: 10)"
                value={customNumber}
                onChange={(e) => setCustomNumber(e.target.value.replace(/\D/g, ''))}
                className="bg-transparent border border-zinc-200 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none"
                maxLength={3}
                data-testid="custom-number"
              />
            </div>
            {(customName || customNumber) && (
              <p className="text-xs text-[#FF3B30] font-bold mt-2 font-body" data-testid="custom-price">
                + {((customName ? 2.99 : 0) + (customNumber ? 2.99 : 0)).toFixed(2)} € de personnalisation
              </p>
            )}
          </div>

          {/* Total price display */}
          {(customName || customNumber) && (
            <div className="mb-6 bg-zinc-50 border border-zinc-200 px-4 py-3 flex items-center justify-between" data-testid="total-price">
              <span className="text-sm text-zinc-500 font-body">Total unitaire</span>
              <span className="text-lg font-bold text-zinc-900 font-body">{(product.price + (customName ? 2.99 : 0) + (customNumber ? 2.99 : 0)).toFixed(2)} €</span>
            </div>
          )}

          {/* Quantity + Add to cart */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center border border-zinc-200">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 text-zinc-400 hover:text-zinc-900" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="w-10 text-center text-sm font-bold" data-testid="qty-value">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="p-2 text-zinc-400 hover:text-zinc-900" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
            <button
              onClick={handleAddToCart}
              className="btn-primary flex-1 bg-zinc-900 text-white font-bold text-sm py-3 uppercase tracking-wider flex items-center justify-center gap-2"
              data-testid="add-to-cart-button"
            >
              <ShoppingBag size={16} /> Ajouter au panier
            </button>
            <button onClick={handleWishlist} className="border border-zinc-200 p-3 text-zinc-400 hover:text-zinc-900 hover:border-zinc-400 transition-colors" data-testid="wishlist-button">
              <Heart size={16} />
            </button>
          </div>

          {/* Meta */}
          <div className="space-y-2 text-xs text-zinc-500 border-t border-zinc-200 pt-4">
            <p><span className="text-zinc-400">Catégorie :</span> {product.category}</p>
            <p><span className="text-zinc-400">Ligue :</span> {product.league}</p>
            <p><span className="text-zinc-400">Version :</span> {product.version === 'joueur' ? 'Joueur' : 'Fan'}</p>
            <p><span className="text-zinc-400">Saison :</span> {product.season}</p>
          </div>

          {/* Description */}
          <div className="mt-6 border-t border-zinc-200 pt-6">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Description</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{product.description}</p>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-16 border-t border-zinc-200 pt-12" data-testid="related-products">
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight uppercase mb-8">Vous aimerez aussi</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-zinc-200">
            {related.map((p, i) => (
              <Link key={p.product_id} to={`/produit/${p.slug}`} className="product-card border border-zinc-200 bg-white block">
                <div className="aspect-square overflow-hidden bg-zinc-900">
                  <ProductImg src={p.images[0]} alt={p.name} width={280} />
                </div>
                <div className="p-3">
                  <p className="text-xs text-zinc-500 font-body uppercase tracking-wider mb-1">{p.team}</p>
                  <h3 className="text-sm font-medium text-zinc-900 font-body leading-tight mb-2 line-clamp-2">{p.name}</h3>
                  <span className="text-sm font-bold text-zinc-900">{p.price.toFixed(2)} €</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
